import { apiClient } from './client'
import type { ApiResponse } from '../types/api'
import type { DepartmentReportParams } from '../types/departmentReports'
import type {
  ConversationPreview,
  ConversationView,
  EnquiryChat,
  MessageCenterDashboardStats,
  MessageCenterReport,
  MessageCenterUserPreview,
  MessageSearchResult,
} from '../types/messageCenter'

export const messageCenterApi = {
  checkAccess: () => apiClient.get<ApiResponse<{ allowed: boolean }>>('/admin/message-center/access'),

  /** Whether this admin currently has an unlocked session (see `unlock`). */
  checkSession: () => apiClient.get<ApiResponse<{ unlocked: boolean }>>('/admin/message-center/session'),

  /** Verifies a fresh 2FA code and opens an unlocked session for this visit. */
  unlock: (data: { code: string }) => apiClient.post<ApiResponse<void>>('/admin/message-center/unlock', data),

  /** Platform-wide chat usage stats — the landing view after unlocking. */
  getDashboard: () => apiClient.get<ApiResponse<MessageCenterDashboardStats>>('/admin/message-center/dashboard'),

  /** Period-vs-period comparison report — the windowable subset of the
   *  dashboard stats above, same shape as the other admin report pages. */
  getReport: (params: DepartmentReportParams) =>
    apiClient.get<ApiResponse<MessageCenterReport>>('/admin/message-center/report', { params }),

  exportReport: (params: DepartmentReportParams) =>
    apiClient.get<Blob>('/admin/message-center/report/export', { params, responseType: 'blob' }),

  /** Look up a user by name or username — independent of Users-section role
   *  permission, see the route's own comment. */
  searchUsers: (q: string) =>
    apiClient.get<ApiResponse<MessageCenterUserPreview[]>>('/admin/message-center/user-search', { params: { q } }),

  /** A user's conversations, most recent first. */
  getUserConversations: (userId: string) =>
    apiClient.get<ApiResponse<ConversationPreview[]>>(`/admin/message-center/users/${userId}/conversations`),

  search: (params: { query: string; userId?: string }) =>
    apiClient.get<ApiResponse<MessageSearchResult[]>>('/admin/message-center/search', { params }),

  /** An enquiry's buyer and seller and their direct chat ("Open chat" on the OneMall enquiries table). */
  getEnquiryChat: (enquiryId: string) =>
    apiClient.get<ApiResponse<EnquiryChat>>(`/admin/message-center/enquiries/${enquiryId}/chat`),

  getConversation: (conversationId: string) =>
    apiClient.get<ApiResponse<ConversationView>>(`/admin/message-center/conversations/${conversationId}`),

  /** One conversation as a ZIP (transcript.html, conversation.json, media/).
   *  Needs a fresh authenticator code on top of the unlocked session. */
  exportConversation: (conversationId: string, data: { code: string; perspectiveUserId?: string }) =>
    apiClient.post<Blob>(`/admin/message-center/conversations/${conversationId}/export`, data, {
      responseType: 'blob',
    }),

  /** Every conversation the user is in, as one ZIP with an index page. */
  exportUserChats: (userId: string, data: { code: string }) =>
    apiClient.post<Blob>(`/admin/message-center/users/${userId}/export`, data, {
      responseType: 'blob',
    }),
}
